/**
 * AI Recruit - Resume Formatter Templates - Blueprint Map Editor
 *
 * The deterministic_docx replacement for DocumentMapEditor.tsx, restyled to
 * match that editor's familiar document-view + Variables-panel + click/drag
 * rebind workflow - WITHOUT reintroducing template_html or html_to_docx.
 * There is still no contenteditable HTML surface and no structural editing
 * (new sections, reordering, rich-text formatting, new tables): every
 * location rendered here is an EXISTING blueprint location (scalar
 * paragraph, table cell, or repeat group) extracted from the uploaded DOCX
 * itself, and this editor only ever changes WHICH concept/text applies at
 * that EXISTING location - rebind it, give it fixed/static text, or mark it
 * intentionally unbound. Saving still only ever writes to the same
 * bindings/literal_overrides/unbound_anchors model save_blueprint_map
 * already persists; this file only changed how that same state is
 * presented and edited.
 *
 * Known simplification vs the old editor: binding happens at the SAME
 * granularity the deterministic population engine already anchors at - one
 * whole paragraph or table cell, never a sub-span within one. A paragraph
 * like "Name : Johnson Srivastava" is one location, so once bound it
 * displays as a single "{{personal_info.full_name}}" tag for the whole
 * paragraph (population.py's own label-prefix preservation still keeps
 * "Name :" in the generated document - this editor just can't show that
 * split visually, since the blueprint model doesn't carry it). Rich-text
 * formatting, inserting a brand-new repeating section, and table
 * insert/delete are not offered - they would require editing the uploaded
 * DOCX's actual structure, which the deterministic architecture forbids;
 * re-upload the source document instead.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { XMarkIcon, InformationCircleIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  fetchBlueprintMap,
  saveBlueprintMap,
  previewResumeFormatTemplate,
  testResumeFormatTemplateWithResume,
  type BlueprintLocation,
} from '@/services/resumeTemplateApi'
import JsonTreeNode, { type JsonTreeDragPayload, type JsonValue } from './JsonTreeNode'

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { detail?: string } } }).response
    if (response?.data?.detail) return response.data.detail
  }
  return fallback
}

interface BlueprintMapEditorProps {
  templateId: string
  onClose: () => void
}

type Mode = 'unbound' | 'bound' | 'literal'

interface RowState {
  location: BlueprintLocation
  mode: Mode
  concept: string
  literalText: string
}

interface IndexedRow extends RowState {
  index: number
}

interface VariableOption {
  path: string
  label: string
}

const modeFor = (loc: BlueprintLocation): Mode => {
  if (loc.is_unbound) return 'unbound'
  if (loc.literal_text !== null && loc.literal_text !== undefined) return 'literal'
  return 'bound'
}

const isPlainObject = (value: JsonValue): value is { [key: string]: JsonValue } =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

// Ported from DocumentMapEditor.tsx - same "what can I bind here" derivation
// straight off the real resume_analysis JSON, no hardcoded field list.
function flattenScalarFields(obj: JsonValue, prefix = ''): VariableOption[] {
  const results: VariableOption[] = []
  if (!isPlainObject(obj)) return results
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    const path = prefix ? `${prefix}.${key}` : key
    if (value === null || typeof value !== 'object') {
      results.push({ path, label: path })
    } else if (!Array.isArray(value)) {
      results.push(...flattenScalarFields(value, path))
    }
  }
  return results
}

function flattenArrayFields(obj: JsonValue, prefix = ''): VariableOption[] {
  const results: VariableOption[] = []
  if (!isPlainObject(obj)) return results
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    const path = prefix ? `${prefix}.${key}` : key
    if (Array.isArray(value)) {
      results.push({ path, label: path })
    } else if (value !== null && typeof value === 'object') {
      results.push(...flattenArrayFields(value, path))
    }
  }
  return results
}

function resolveArrayItemShape(arrayPath: string, root: JsonValue): Record<string, JsonValue> | null {
  if (!arrayPath.trim()) return null
  let node: JsonValue = root
  for (const part of arrayPath.split('.')) {
    if (!isPlainObject(node)) return null
    node = node[part] ?? null
  }
  if (!Array.isArray(node) || !node.length) return null
  const first = node[0]
  return isPlainObject(first) ? first : null
}

// --- Document-order reconstruction from the flat locations list ------------
// The backend exposes block_index (and row/col for table cells) purely so
// this editor can rebuild the uploaded document's own reading order and
// table grouping - never used for binding/population itself.

type DocItem =
  | { type: 'paragraph'; row: IndexedRow }
  | { type: 'table'; blockIndex: number; grid: IndexedRow[][] }
  | { type: 'repeat_group'; row: IndexedRow; nested: DocItem[] }

function groupId(location: BlueprintLocation): string {
  return location.location_id.slice('repeat_group:'.length)
}

function buildDocItems(entries: IndexedRow[], nestedByGroup: Map<string, IndexedRow[]>): DocItem[] {
  const order: number[] = []
  const byBlock = new Map<number, IndexedRow[]>()
  for (const r of entries) {
    const bi = r.location.block_index ?? -1
    if (!byBlock.has(bi)) {
      byBlock.set(bi, [])
      order.push(bi)
    }
    byBlock.get(bi)!.push(r)
  }
  order.sort((a, b) => a - b)

  const items: DocItem[] = []
  for (const bi of order) {
    const group = byBlock.get(bi)!
    const first = group[0]
    if (first.location.kind === 'repeat_group') {
      const nestedEntries = nestedByGroup.get(groupId(first.location)) ?? []
      items.push({ type: 'repeat_group', row: first, nested: buildDocItems(nestedEntries, nestedByGroup) })
    } else if (first.location.kind === 'table_cell') {
      const byRow = new Map<number, IndexedRow[]>()
      for (const cell of group) {
        const ri = cell.location.row ?? 0
        if (!byRow.has(ri)) byRow.set(ri, [])
        byRow.get(ri)!.push(cell)
      }
      const rowIndices = Array.from(byRow.keys()).sort((a, b) => a - b)
      const grid = rowIndices.map((ri) => byRow.get(ri)!.sort((a, b) => (a.location.col ?? 0) - (b.location.col ?? 0)))
      items.push({ type: 'table', blockIndex: bi, grid })
    } else {
      items.push({ type: 'paragraph', row: first })
    }
  }
  return items
}

// Matches how the generated .docx will actually look - ported from
// DocumentMapEditor.tsx's DOC_STYLES (only the docx-preview rules; this
// editor has no contenteditable surface to style).
const DOC_STYLES = `
.dme-docx-preview .docx-wrapper { background: #fff; padding: 16px 0; }
.dme-docx-preview .docx-page { margin: 0 auto 16px; box-shadow: 0 0 6px rgba(0,0,0,0.15); }
`

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const BlueprintMapEditor: React.FC<BlueprintMapEditorProps> = ({ templateId, onClose }) => {
  const queryClient = useQueryClient()
  const [rows, setRows] = useState<RowState[]>([])

  const docxPreviewContainerRef = useRef<HTMLDivElement | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [variablesCollapsed, setVariablesCollapsed] = useState(false)
  const [variablesWidth, setVariablesWidth] = useState(380)
  const resizingRef = useRef(false)
  const [previewing, setPreviewing] = useState(false)
  const [previewRendered, setPreviewRendered] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const [popover, setPopover] = useState<{ index: number; x: number; y: number } | null>(null)
  const [popoverSearch, setPopoverSearch] = useState('')
  const [popoverLiteralDraft, setPopoverLiteralDraft] = useState('')

  const [testJsonModalOpen, setTestJsonModalOpen] = useState(false)
  const [testJsonDraft, setTestJsonDraft] = useState('')
  const [helpModalOpen, setHelpModalOpen] = useState(false)

  const handleResizeStart = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    resizingRef.current = true
    document.body.style.cursor = 'col-resize'
  }, [])

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!resizingRef.current) return
      // Leave at least 320px for the document panel on the left; otherwise
      // let the admin drag this as wide as they need to read Preview/Variables.
      const maxWidth = Math.max(280, window.innerWidth - 320)
      setVariablesWidth(Math.min(maxWidth, Math.max(280, window.innerWidth - event.clientX)))
    }
    const handleMouseUp = () => {
      if (!resizingRef.current) return
      resizingRef.current = false
      document.body.style.cursor = ''
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  const { data, isLoading, error } = useQuery({
    queryKey: ['resume-format-template-blueprint-map', templateId],
    queryFn: () => fetchBlueprintMap(templateId),
  })

  useEffect(() => {
    if (!data) return
    setRows(
      data.locations.map((location) => ({
        location,
        mode: modeFor(location),
        concept: location.bound_concept ?? '',
        literalText: location.literal_text ?? '',
      }))
    )
  }, [data])

  const updateRow = (index: number, patch: Partial<RowState>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const buildTarget = (location: BlueprintLocation, concept: string) => {
    if (location.kind === 'repeat_group') {
      return { concept, target: { kind: 'repeat_group', repeat_group_id: groupId(location) } }
    }
    const anchor = JSON.parse(location.location_id)
    return { concept, target: { kind: 'paragraph', anchor } }
  }

  const arrayConceptSet = useMemo(() => new Set(data?.array_concepts ?? []), [data?.array_concepts])

  const saveMutation = useMutation({
    mutationFn: (activate: boolean) => {
      const bindings: { concept: string; target: Record<string, unknown> }[] = []
      const literal_overrides: { anchor: Record<string, unknown>; text: string }[] = []
      const unbound_anchors: { anchor: Record<string, unknown> }[] = []
      // A list concept (education, certifications, professional_experience,
      // core_competencies, ...) bound across SEVERAL locations must be
      // saved as ONE paragraph_list binding carrying every one of those
      // locations as its own anchor - never as several independent scalar
      // "paragraph" bindings, which the render-time safety guard correctly
      // (but silently) drops as unsafe for a list concept, emptying the
      // whole section. Collected here, per concept, before being turned
      // into bindings below - a scalar concept bound at more than one
      // location (e.g. the same name repeated in a header and a footer)
      // is NOT affected: it keeps the old one-binding-per-location
      // behavior, since each of its locations is independently a real,
      // complete value, not one item of a list.
      const listAnchorsByConcept = new Map<string, Record<string, unknown>[]>()

      for (const row of rows) {
        if (row.location.kind === 'repeat_group') {
          if (row.mode === 'bound' && row.concept.trim()) {
            bindings.push(buildTarget(row.location, row.concept.trim()))
          }
          continue
        }
        const anchor = JSON.parse(row.location.location_id)
        if (row.mode === 'unbound') {
          unbound_anchors.push({ anchor })
        } else if (row.mode === 'literal') {
          if (row.literalText.trim()) {
            literal_overrides.push({ anchor, text: row.literalText })
          }
        } else if (row.mode === 'bound' && row.concept.trim()) {
          const concept = row.concept.trim()
          if (arrayConceptSet.has(concept)) {
            const list = listAnchorsByConcept.get(concept) ?? []
            list.push(anchor)
            listAnchorsByConcept.set(concept, list)
          } else {
            bindings.push(buildTarget(row.location, concept))
          }
        }
      }

      for (const [concept, anchors] of listAnchorsByConcept) {
        bindings.push({ concept, target: { kind: 'paragraph_list', anchors } })
      }

      return saveBlueprintMap(templateId, { bindings, literal_overrides, unbound_anchors, activate })
    },
    onSuccess: (_template, activate) => {
      toast.success(activate ? 'Saved and activated' : 'Saved as draft')
      queryClient.invalidateQueries({ queryKey: ['resume-format-template-blueprint-map', templateId] })
      queryClient.invalidateQueries({ queryKey: ['resume-format-templates'] })
      onClose()
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'Failed to save')
    },
  })

  // Renders the actual generated docx inline via docx-preview (same
  // approach DocumentMapEditor.tsx uses) - the deterministic engine's real
  // output with current bindings applied, never the original uploaded file.
  const refreshInlinePreview = useCallback(async () => {
    setPreviewing(true)
    setPreviewError(null)
    setPreviewRendered(false)
    try {
      const blob = await previewResumeFormatTemplate(templateId)
      const arrayBuffer = await blob.arrayBuffer()
      const container = docxPreviewContainerRef.current
      if (!container) {
        setPreviewing(false)
        setPreviewError('Failed to render preview.')
        return
      }
      container.innerHTML = ''
      try {
        const docxModule = await import('docx-preview')
        await docxModule.renderAsync(arrayBuffer, container)
        setPreviewRendered(true)
      } catch {
        setPreviewError('Failed to render an inline preview.')
      } finally {
        setPreviewing(false)
      }
    } catch (err) {
      setPreviewing(false)
      setPreviewError(getErrorMessage(err, 'Failed to generate preview.'))
    }
  }, [templateId])

  const downloadPreview = async () => {
    try {
      const blob = await previewResumeFormatTemplate(templateId)
      downloadBlob(blob, 'template_preview.docx')
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to preview template')
    }
  }

  const togglePreview = () => {
    setPreviewOpen((open) => {
      const next = !open
      if (next && !previewRendered && !previewing) {
        // Fire-and-forget: opening the panel for the first time renders it
        // immediately instead of leaving the admin to find the Refresh button.
        void refreshInlinePreview()
      }
      return next
    })
  }

  const testMutation = useMutation({
    mutationFn: (resumeAnalysis: Record<string, unknown>) => testResumeFormatTemplateWithResume(templateId, resumeAnalysis),
    onSuccess: (blob) => {
      downloadBlob(blob, 'test_formatted_resume.docx')
      toast.success('Test resume generated and downloaded.')
      setTestJsonModalOpen(false)
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Failed to format the test resume.')),
  })

  const submitTestJson = () => {
    let resumeAnalysis: Record<string, unknown>
    try {
      resumeAnalysis = JSON.parse(testJsonDraft) as Record<string, unknown>
    } catch {
      toast.error('That is not valid JSON. Please check the payload and try again.')
      return
    }
    testMutation.mutate(resumeAnalysis)
  }

  // --- Drag-and-drop from the Variables panel onto any location -------------

  const handleConceptDrop = (index: number, row: RowState) => (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const raw = event.dataTransfer.getData('application/json')
    if (!raw) return
    let payload: JsonTreeDragPayload
    try {
      payload = JSON.parse(raw) as JsonTreeDragPayload
    } catch {
      return
    }
    const isArrayField = payload.kind === 'array-of-objects' || payload.kind === 'array-of-primitives'
    if (row.location.kind === 'repeat_group' && !isArrayField) {
      toast('A repeating section binds to a LIST field - drag the "+ Repeat Section" chip for a field, not a single value.', {
        icon: '⚠️',
      })
      return
    }
    if (row.location.kind !== 'repeat_group' && isArrayField) {
      toast('This location holds a single value - drag a plain field, not a list field.', { icon: '⚠️' })
      return
    }
    updateRow(index, { mode: 'bound', concept: payload.insertValue })
  }

  // --- Click-to-rebind popover ------------------------------------------

  const openPopover = (index: number, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setPopover({ index, x: Math.min(event.clientX, window.innerWidth - 300), y: Math.min(event.clientY, window.innerHeight - 380) })
    setPopoverSearch('')
    setPopoverLiteralDraft(rows[index]?.literalText ?? '')
  }

  const closePopover = () => setPopover(null)

  const exampleAnalysisJson = (data?.example_analysis_json ?? null) as unknown as JsonValue

  const popoverOptions = useMemo<VariableOption[]>(() => {
    if (!popover) return []
    const row = rows[popover.index]
    if (!row) return []
    let all: VariableOption[]
    if (row.location.kind === 'repeat_group') {
      all = flattenArrayFields(exampleAnalysisJson)
    } else if (row.location.within_repeat_group) {
      const groupRow = rows.find((r) => r.location.location_id === `repeat_group:${row.location.within_repeat_group}`)
      const shape = groupRow ? resolveArrayItemShape(groupRow.concept, exampleAnalysisJson) : null
      all = shape ? flattenScalarFields(shape) : flattenScalarFields(exampleAnalysisJson)
    } else {
      all = flattenScalarFields(exampleAnalysisJson)
    }
    const term = popoverSearch.trim().toLowerCase()
    if (!term) return all
    return all.filter((f) => f.path.toLowerCase().includes(term))
  }, [popover, rows, popoverSearch, exampleAnalysisJson])

  const objectKeys = isPlainObject(exampleAnalysisJson) ? Object.keys(exampleAnalysisJson) : []

  const boundCount = rows.filter((r) => r.mode === 'bound' && r.concept.trim()).length
  const repeatGroupCount = rows.filter((r) => r.location.kind === 'repeat_group').length

  const docItems = useMemo(() => {
    const indexed: IndexedRow[] = rows.map((row, index) => ({ ...row, index }))
    const nestedByGroup = new Map<string, IndexedRow[]>()
    const topLevel: IndexedRow[] = []
    for (const r of indexed) {
      if (r.location.kind !== 'repeat_group' && r.location.within_repeat_group) {
        const list = nestedByGroup.get(r.location.within_repeat_group) ?? []
        list.push(r)
        nestedByGroup.set(r.location.within_repeat_group, list)
      } else {
        topLevel.push(r)
      }
    }
    return buildDocItems(topLevel, nestedByGroup)
  }, [rows])

  // --- One location's rendered, clickable/droppable box -------------------

  const LocationBox: React.FC<{ row: IndexedRow; isGroupHeader?: boolean }> = ({ row, isGroupHeader }) => {
    const shared = {
      onClick: (e: React.MouseEvent) => openPopover(row.index, e),
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      },
      onDrop: handleConceptDrop(row.index, row),
      title: 'Click to rebind, or drag a field from Variables here',
    }
    if (row.mode === 'bound' && row.concept.trim()) {
      return (
        <span
          {...shared}
          className="inline-block cursor-pointer rounded border border-dashed border-emerald-400 bg-emerald-50 px-1 text-emerald-900 hover:bg-emerald-100"
        >
          {`{{${row.concept}}}`}
        </span>
      )
    }
    if (row.mode === 'literal') {
      return (
        <span
          {...shared}
          className="inline-block cursor-pointer rounded border border-dashed border-amber-400 bg-amber-50 px-1 text-amber-900 hover:bg-amber-100"
        >
          {row.literalText || <em className="text-amber-500">(empty fixed text)</em>}
        </span>
      )
    }
    if (isGroupHeader) {
      return (
        <span {...shared} className="inline-block cursor-pointer rounded px-1 text-xs text-indigo-500 hover:bg-indigo-50">
          (drag a list field here, or click to bind this repeating section)
        </span>
      )
    }
    return (
      <span {...shared} className="inline-block cursor-pointer rounded px-1 text-gray-700 hover:bg-gray-100">
        {row.location.text_preview || <em className="text-gray-400">(empty)</em>}
      </span>
    )
  }

  const renderDocItems = (items: DocItem[]): React.ReactNode =>
    items.map((item) => {
      if (item.type === 'paragraph') {
        return (
          <div
            key={item.row.index}
            className={item.row.location.is_heading ? 'mb-1 mt-3 text-base font-bold text-gray-900' : 'mb-1 text-sm'}
          >
            <LocationBox row={item.row} />
          </div>
        )
      }
      if (item.type === 'table') {
        return (
          <table key={`table-${item.blockIndex}`} className="my-2 w-full border-collapse text-sm">
            <tbody>
              {item.grid.map((cells, ri) => (
                <tr key={ri}>
                  {cells.map((cell) => (
                    <td key={cell.index} className="border border-gray-300 p-1 align-top">
                      <LocationBox row={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
      return (
        <div key={item.row.index} className="relative my-3 rounded-md border border-dashed border-indigo-400 p-3 pt-4">
          <span className="absolute -top-2.5 left-2 bg-white px-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
            Repeats ×{item.row.location.instance_count ?? '?'}
          </span>
          <div className="mb-2 text-sm">
            <LocationBox row={item.row} isGroupHeader />
          </div>
          <div className="space-y-1 border-l-2 border-indigo-100 pl-3">{renderDocItems(item.nested)}</div>
        </div>
      )
    })

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <p className="text-red-600">Failed to load this template&apos;s blueprint map.</p>
        <button onClick={onClose} className="mt-4 text-sm text-gray-600 underline">
          Close
        </button>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
      <style>{DOC_STYLES}</style>
      <div className="flex items-center justify-between border-b bg-white px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Edit Template</h1>
          <p className="text-sm text-gray-500">
            Confirm or correct every field before this template goes live. The uploaded document&apos;s own layout is
            the source of truth - structural edits (new sections, reordering, formatting) aren&apos;t supported here;
            re-upload the source document instead.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => saveMutation.mutate(false)}
            disabled={saveMutation.isPending}
            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Save as Draft
          </button>
          <button
            onClick={() => saveMutation.mutate(true)}
            disabled={saveMutation.isPending}
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Save as Active
          </button>
          <button onClick={onClose} className="rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 border-b bg-white px-6 py-2">
        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
          {boundCount} Variable{boundCount === 1 ? '' : 's'} Bound
        </span>
        <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
          {repeatGroupCount} Repeat Section{repeatGroupCount === 1 ? '' : 's'}
        </span>
        <span className="text-xs text-gray-400">Click a field to rebind it, or drag one in from Variables.</span>
        <button
          onClick={() => setHelpModalOpen(true)}
          className="ml-auto rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
        >
          Help
        </button>
        <button
          onClick={() => setTestJsonModalOpen(true)}
          className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
        >
          Test with Resume Analysis JSON
        </button>
        <button onClick={togglePreview} className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
          {previewOpen ? 'Close Preview' : 'Preview'}
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto bg-gray-50 px-6 py-4" onClick={closePopover}>
          <div className="dme-doc min-h-full rounded-md border border-gray-200 bg-white p-5 shadow-sm">
            {renderDocItems(docItems)}
          </div>
        </div>

        {!variablesCollapsed && (
          <div
            onMouseDown={handleResizeStart}
            title="Drag to resize"
            className="w-1.5 flex-shrink-0 cursor-col-resize bg-gray-200 transition-colors hover:bg-primary-400 active:bg-primary-500"
          />
        )}

        <div
          className={`flex min-h-0 flex-shrink-0 flex-col border-l bg-white ${variablesCollapsed ? 'w-9' : ''}`}
          style={variablesCollapsed ? undefined : { width: variablesWidth }}
        >
          {variablesCollapsed ? (
            <button
              onClick={() => setVariablesCollapsed(false)}
              title="Show Variables panel"
              className="flex h-full w-full flex-col items-center gap-2 pt-3 text-gray-500 hover:bg-gray-50"
            >
              <ChevronLeftIcon className="h-5 w-5" />
              <span className="text-xs font-semibold [writing-mode:vertical-rl]">Variables</span>
            </button>
          ) : (
          <>
          {previewOpen && (
            <div className="flex h-1/2 min-h-0 flex-col border-b">
              <div className="flex items-center justify-between border-b px-4 py-2">
                <h2 className="text-sm font-semibold text-gray-900">Preview</h2>
                <div className="flex gap-2">
                  <button
                    onClick={refreshInlinePreview}
                    disabled={previewing}
                    className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {previewing ? 'Rendering…' : 'Refresh'}
                  </button>
                  <button
                    onClick={downloadPreview}
                    className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    Download
                  </button>
                </div>
              </div>
              <div className="dme-docx-preview flex-1 overflow-auto bg-gray-50 p-2">
                {!previewRendered && !previewing && !previewError && (
                  <p className="p-3 text-xs text-gray-400">Rendering the current bindings…</p>
                )}
                {previewError && <p className="p-3 text-xs text-red-600">{previewError}</p>}
                <div ref={docxPreviewContainerRef} />
              </div>
            </div>
          )}
          <div className={`flex min-h-0 flex-col ${previewOpen ? 'h-1/2' : 'flex-1'}`}>
            <div className="flex items-center gap-1.5 border-b px-4 py-2">
              <button
                onClick={() => setVariablesCollapsed(true)}
                title="Collapse Variables panel"
                className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
              <h2 className="text-sm font-semibold text-gray-900">Variables</h2>
              <span title="This is the actual resume_analysis JSON used to build this template. Drag a field onto the document to bind it.">
                <InformationCircleIcon className="h-4 w-4 cursor-help text-gray-400" />
              </span>
            </div>
            <div className="flex-1 overflow-auto px-2.5 py-2">
              {exampleAnalysisJson ? (
                objectKeys.map((k) => (
                  <JsonTreeNode
                    key={k}
                    keyLabel={k}
                    value={(exampleAnalysisJson as Record<string, JsonValue>)[k]}
                    absolutePath={k}
                    depth={0}
                  />
                ))
              ) : (
                <p className="p-3 text-xs text-gray-500">
                  No example resume data is stored for this template yet - re-prepare it to capture one.
                </p>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      {popover && (
        <div className="fixed inset-0 z-40" onClick={closePopover}>
          <div
            className="absolute z-50 w-72 rounded-md border border-gray-200 bg-white shadow-lg"
            style={{ left: popover.x, top: popover.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-xs font-semibold text-gray-700">
                {rows[popover.index]?.location.kind === 'repeat_group' ? 'Bind repeating section to a list' : 'Bind this location'}
              </span>
              <button onClick={closePopover} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="p-2">
              <input
                autoFocus
                className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Search, or type a custom path and press Enter"
                value={popoverSearch}
                onChange={(e) => setPopoverSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && popoverSearch.trim()) {
                    updateRow(popover.index, { mode: 'bound', concept: popoverSearch.trim() })
                    closePopover()
                  }
                }}
              />
              <div className="max-h-40 overflow-auto">
                {popoverOptions.length === 0 && (
                  <p className="px-1 py-1 text-xs text-gray-400">No matching fields - press Enter to use the typed path.</p>
                )}
                {popoverOptions.map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    className="block w-full truncate rounded px-1.5 py-1 text-left text-xs text-gray-800 hover:bg-gray-100"
                    onClick={() => {
                      updateRow(popover.index, { mode: 'bound', concept: f.path })
                      closePopover()
                    }}
                    title={f.path}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {rows[popover.index]?.location.kind !== 'repeat_group' && (
                <>
                  <div className="my-2 border-t" />
                  <textarea
                    className="mb-1 w-full rounded border border-gray-300 px-2 py-1 text-xs"
                    rows={2}
                    placeholder="Or set fixed text shown on every generated resume"
                    value={popoverLiteralDraft}
                    onChange={(e) => setPopoverLiteralDraft(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        updateRow(popover.index, { mode: 'literal', literalText: popoverLiteralDraft })
                        closePopover()
                      }}
                    >
                      Set fixed text
                    </button>
                    <button
                      type="button"
                      className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        updateRow(popover.index, { mode: 'unbound' })
                        closePopover()
                      }}
                    >
                      Mark unbound
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {testJsonModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setTestJsonModalOpen(false)}>
          <div className="w-[520px] rounded-md bg-white p-4 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Test with Resume Analysis JSON</h3>
            <textarea
              className="h-64 w-full rounded border border-gray-300 p-2 font-mono text-xs"
              placeholder='{"personal_info": {"full_name": "..."}, ...}'
              value={testJsonDraft}
              onChange={(e) => setTestJsonDraft(e.target.value)}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => setTestJsonModalOpen(false)}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitTestJson}
                disabled={testMutation.isPending}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {testMutation.isPending ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {helpModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setHelpModalOpen(false)}>
          <div
            className="max-h-[80vh] w-[560px] overflow-auto rounded-md bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">How to use this editor</h3>
              <button onClick={() => setHelpModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 text-xs text-gray-700">
              <div>
                <p className="font-semibold text-gray-900">Binding a field</p>
                <p className="mt-0.5">
                  Click any highlighted box in the document, or drag a field from the Variables panel onto it. A
                  single document line binds to one field (e.g. a name or email). A section marked &quot;REPEATS&quot;
                  binds to a field tagged <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">array</span> in
                  Variables - drag its <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-900">+ Repeat Section</span> chip,
                  not a single value from inside it.
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Never bind a true/false value</p>
                <p className="mt-0.5">
                  In Variables, a field showing plainly <code className="rounded bg-gray-100 px-1">true</code> or{' '}
                  <code className="rounded bg-gray-100 px-1">false</code> (no <span className="rounded-full bg-amber-50 px-1 text-[10px] font-medium text-amber-800">array</span> badge)
                  is a flag, not text - binding it will print the literal word &quot;True&quot; or &quot;False&quot; on the resume.
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Don&apos;t bind onto a section heading</p>
                <p className="mt-0.5">
                  If a line looks like a section title (e.g. &quot;Core Competencies&quot;, ALL CAPS, or short bold
                  text), it&apos;s almost always structural, not a data field - bind the real content lines below it
                  instead.
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Seeing &quot;Sample content for this field.&quot;</p>
                <p className="mt-0.5">
                  This means a binding isn&apos;t resolving to real data, usually because a list (education,
                  certifications, experience, etc.) got bound incorrectly. Rebind every row of that repeating
                  section to the same field and Save again - this editor automatically groups same-field rows back
                  into one list binding on save.
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Save as Draft vs. Save as Active</p>
                <p className="mt-0.5">
                  Draft keeps your changes private for further review. Active makes this the live template used
                  whenever this client&apos;s resumes are downloaded formatted.
                </p>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Resizing &amp; Preview</p>
                <p className="mt-0.5">
                  Drag the vertical bar between the document and the Variables panel to resize it. Click
                  &quot;Preview&quot; to render the actual document with your current bindings before saving.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default BlueprintMapEditor
